import React, { useState, useEffect, useRef } from 'react';

interface AnimatedTitleProps {
  text: string;
  animationType?: 'typewriter' | 'fadeInUp' | 'glowPulse' | 'letterByLetter' | 'gradientShift';
  duration?: number;
  delay?: number;
  className?: string;
}

const AnimatedTitle: React.FC<AnimatedTitleProps> = ({
  text,
  animationType = 'fadeInUp',
  duration = 1000,
  delay = 0,
  className = '',
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [displayText, setDisplayText] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [animationComplete, setAnimationComplete] = useState(false);
  const elementRef = useRef<HTMLDivElement>(null);

  // 检查是否应该减少动画
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, delay);

    return () => clearTimeout(timer);
  }, [delay]);

  // 动画完成后的清理
  useEffect(() => {
    if (isVisible && !prefersReducedMotion) {
      const timer = setTimeout(() => {
        setAnimationComplete(true);
        // 移除will-change属性以优化性能
        if (elementRef.current) {
          elementRef.current.classList.add('animation-complete');
        }
      }, duration + delay);

      return () => clearTimeout(timer);
    }
  }, [isVisible, duration, delay, prefersReducedMotion]);

  // 打字机效果
  useEffect(() => {
    if (animationType === 'typewriter' && isVisible && !prefersReducedMotion) {
      if (currentIndex < text.length) {
        const timer = setTimeout(() => {
          setDisplayText(text.slice(0, currentIndex + 1));
          setCurrentIndex(currentIndex + 1);
        }, duration / text.length);
        return () => clearTimeout(timer);
      }
    } else {
      setDisplayText(text);
    }
  }, [animationType, isVisible, currentIndex, text, duration, prefersReducedMotion]);

  // 逐字母动画
  const renderLetterByLetter = () => {
    return text.split('').map((char, index) => (
      <span
        key={index}
        className={`inline-block animate-letter-fade ${prefersReducedMotion ? '' : 'opacity-0'}`}
        style={{
          animationDelay: prefersReducedMotion ? '0s' : `${delay + index * 100}ms`,
          opacity: prefersReducedMotion ? 1 : undefined,
        }}
      >
        {char === ' ' ? '\u00A0' : char}
      </span>
    ));
  };

  const getAnimationClass = () => {
    if (prefersReducedMotion) {
      return 'opacity-100';
    }

    switch (animationType) {
      case 'typewriter':
        return isVisible ? 'animate-typewriter' : 'opacity-0';
      case 'fadeInUp':
        return isVisible ? 'animate-title-fade-up' : 'opacity-0';
      case 'glowPulse':
        return isVisible ? 'animate-title-glow' : 'opacity-0';
      case 'gradientShift':
        return isVisible ? 'animate-gradient-shift' : 'opacity-0';
      case 'letterByLetter':
        return 'opacity-100';
      default:
        return isVisible ? 'animate-title-fade-up' : 'opacity-0';
    }
  };

  const baseClasses = `font-bold transition-opacity duration-300 ${className}`;

  if (animationType === 'letterByLetter') {
    return (
      <div className={baseClasses}>
        {renderLetterByLetter()}
      </div>
    );
  }

  return (
    <div
      ref={elementRef}
      className={`${baseClasses} ${getAnimationClass()}`}
      style={{
        animationDuration: prefersReducedMotion ? '0s' : `${duration}ms`,
      }}
    >
      {animationType === 'typewriter' ? displayText : text}
    </div>
  );
};

export default AnimatedTitle;